/**
 * Main Routes Configuration
 * 
 * Defines all application routes including:
 * - Public pages (help, track order, shared views)
 * - Main portal pages (dashboard, records, analytics)
 * - Admin pages (roles, users, billing, integrations)
 * - Mid-mile and transport partner routes
 * - Compliance and operations routes
 * 
 * @module Core/Routes
 */

import React from 'react'
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom'
import SnackbarProvider from 'react-simple-snackbar'
import '../i18n/i18n'
import GlobalState from './context/globalState'
import MainRoutes from './mainRoutes'

const Routes = () => {
    return (
        <GlobalState>
            <Router>
                <Switch>
                    <SnackbarProvider>
                        {/* PUBLIC PAGES */}
                        <Route exact path="/help" component={MainRoutes} />
                        <Route exact path="/trackorder" component={MainRoutes} />
                        <Route exact path="/trackorder/:trackId" component={MainRoutes} />
                        <Route exact path="/sharedview/:viewId" component={MainRoutes} />
                        <Route exact path="/quickbooks-oauth-callback" component={MainRoutes} />
                        <Route exact path="/review/:tokenId" component={MainRoutes} />
                        {/* MAIN PORTAL PAGES */}
                        <Route exact path="/" component={MainRoutes} />
                        <Route exact path="/dashboard" component={MainRoutes} />
                        <Route exact path="/login" component={MainRoutes} />
                        <Route exact path="/signup" component={MainRoutes} />
                        <Route exact path="/forgotPassword" component={MainRoutes} />
                        <Route exact path="/changePassword" component={MainRoutes} />
                        <Route exact path="/mainContainer" component={MainRoutes} />
                        <Route exact path="/records" component={MainRoutes} />
                        <Route exact path="/orderTable" component={MainRoutes} />
                        <Route exact path="/search" component={MainRoutes} />
                        <Route exact path="/liveConnect" component={MainRoutes} />
                        <Route exact path="/reports" component={MainRoutes} />
                        <Route exact path="/dateRegion" component={MainRoutes} />
                        <Route exact path="/shipments" component={MainRoutes} />
                        <Route exact path="/dispatch" component={MainRoutes} />
                        <Route exact path="/disputes" component={MainRoutes} />
                        <Route exact path="/email-intake" component={MainRoutes} />
                        <Route exact path="/mapFleet" component={MainRoutes} />
                        <Route exact path="/mapFleet/network" component={MainRoutes} />
                        <Route exact path="/mapFleet/routing" component={MainRoutes} />
                        <Route exact path="/routes" component={MainRoutes} />
                        <Route exact path="/analytics" component={MainRoutes} />
                        <Route exact path="/kpis" component={MainRoutes} />
                        <Route exact path="/assets" component={MainRoutes} />
                        {/* ADMIN PAGES */}
                        <Route exact path="/admin" component={MainRoutes} />
                        <Route exact path="/admin/role" component={MainRoutes} />
                        <Route exact path="/admin/userRole" component={MainRoutes} />
                        <Route exact path="/admin/vendor" component={MainRoutes} />
                        <Route exact path="/admin/contractor" component={MainRoutes} />
                        <Route exact path="/admin/distributionZone" component={MainRoutes} />
                        <Route exact path="/admin/notifications" component={MainRoutes} />
                        <Route exact path="/admin/calendarSettings" component={MainRoutes} />
                        <Route exact path="/admin/fields" component={MainRoutes} />
                        <Route exact path="/admin/email-templates" component={MainRoutes} />
                        <Route exact path="/admin/integrations/external" component={MainRoutes} />
                        <Route exact path="/admin/integrations/internal" component={MainRoutes} />
                        <Route exact path="/admin/integrations/quickbooks" component={MainRoutes} />
                        {/* AI PAGES */}
                        <Route exact path="/admin/ai/agent-configuration" component={MainRoutes} />
                        <Route exact path="/admin/ai/shyftbase" component={MainRoutes} />
                        <Route exact path="/admin/ai/optimizer" component={MainRoutes} />
                        {/* BILLING PAGES */}
                        <Route exact path="/admin/billing" component={MainRoutes} />
                        <Route exact path="/admin/billing/ratetable" component={MainRoutes} />
                        <Route exact path="/admin/billing/ratetable/:rateId" component={MainRoutes} />
                        <Route exact path="/admin/billing/taxes" component={MainRoutes} />
                        <Route exact path="/admin/billing/fscrates" component={MainRoutes} />
                        <Route exact path="/admin/billing/weeklyfscrates" component={MainRoutes} />
                        <Route exact path="/admin/billing/services" component={MainRoutes} />
                        <Route exact path="/admin/billing/substatus" component={MainRoutes} />
                        <Route exact path="/admin/billing/conditions" component={MainRoutes} />
                        <Route exact path="/admin/billing/tiersEscrow" component={MainRoutes} />
                        <Route exact path="/admin/automations" component={MainRoutes} />
                        <Route exact path="/admin/billing/globalUnits" component={MainRoutes} />
                        <Route exact path="/admin/billing/fsc" component={MainRoutes} />
                        
                        {/* RATES MANAGEMENT */}
                        <Route exact path="/admin/rates" component={MainRoutes} />
                        <Route exact path="/admin/rates/partner-rate-tables" component={MainRoutes} />
                        {/* MID-MILE ADMIN ROUTES (Legacy - keeping for backward compatibility) */}
                        <Route exact path="/admin/midmile" component={MainRoutes} />
                        <Route exact path="/admin/midmile/rate-tables" component={MainRoutes} />
                        <Route exact path="/admin/midmile/tariffs" component={MainRoutes} />
                        <Route exact path="/admin/midmile/bidding" component={MainRoutes} />
                        <Route exact path="/admin/midmile/settings" component={MainRoutes} />
                        <Route exact path="/admin/midmile/bidding/active" component={MainRoutes} />
                        
                        {/* TRANSPORT PARTNERS MANAGEMENT ROUTES */}
                        <Route exact path="/admin/transport-partners/directory" component={MainRoutes} />
                        <Route exact path="/admin/transport-partners/profiles" component={MainRoutes} />
                        <Route exact path="/admin/transport-partners/drivers" component={MainRoutes} />
                        <Route exact path="/admin/transport-partners/rate-intake" component={MainRoutes} />
                        <Route exact path="/admin/addressBook" component={MainRoutes} />
                        <Route exact path="/admin/services" component={MainRoutes} />
                        <Route exact path="/admin/order-templates" component={MainRoutes} />
                        
                        {/* BILLING & SETTLEMENT ROUTES */}
                        <Route exact path="/admin/billing/audit" component={MainRoutes} />
                        
                        {/* OPERATIONS & ROUTING ROUTES (Phase 1 - Priority 1) */}
                        <Route exact path="/admin/operations/capacity-management" component={MainRoutes} />
                        <Route exact path="/admin/operations/re-optimization" component={MainRoutes} />
                        
                        {/* COMPLIANCE ROUTES (Phase 1 - Priority 1) */}
                        <Route exact path="/admin/compliance/gdpr" component={MainRoutes} />
                        <Route exact path="/admin/compliance/country-rules" component={MainRoutes} />
                        <Route exact path="/admin/compliance/audit-trails" component={MainRoutes} />
                    </SnackbarProvider>
                </Switch>
            </Router>
        </GlobalState>
    )
}
export default React.memo(Routes)
